import { TitleCasePipe } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MessageService } from "primeng/api";
import { AppMainComponent } from "src/app/app.main.component";
import { ObjectNames, Severity } from "src/app/configurations/finance/constants/constants";
import { AccountService } from "src/app/configurations/finance/finance-setup/account/account.service";
import { AddEditDialogType, maxChildLevelForChartOfAccountsCreate } from "src/app/finance/constants/constants";
import { IFinanceAccount } from "src/app/finance/models/finance-account.model";
import { IGroup } from "src/app/finance/models/group.model";
import { FinanceUtilityService } from "src/app/finance/util/finance-utility.service";
import { Status } from "../../models/status";

@Component({
    selector: "app-add-edit-finance-account-modal",
    templateUrl: "./add-edit-finance-account-modal.component.html",
    styleUrls: ["./add-edit-finance-account-modal.component.scss"],
})
export class AddEditFinanceAccountModalComponent implements OnInit {
    @Input() displayAddEditChart;
    @Input() allAccounts;
    @Input() addEditChartHeader;
    @Input() addEditDialogType;
    @Input() editAccountData;

    @Output() onCancel = new EventEmitter();
    @Output() onSuccessfullAddEdit = new EventEmitter();

    accountForm: FormGroup;
    accountConstants = ObjectNames;
    parentAccountType = [];
    chartGroupsData = [];
    noRange = "";
    dialogType = AddEditDialogType;
    parentType: boolean = false;
    childType: boolean = false;
    disabledGroupForSubChild: boolean = false;
    accountTypes = [];
    detailsData;
    detailsDataType = "";
    financeObjectId: number;

    displayAddGroup = false;
    parentId: number = 0;
    filteredGroup: any;
    action: string = "";
    displayNumber: any;
    displayOperator: string = "";
    displayAccNumber: any;
    isDisabled: boolean = false;
    groupForm: FormGroup;
    nonPostingAccount: any;

    constructor(
        public app: AppMainComponent,
        private fb: FormBuilder,
        private accountService: AccountService,
        private messageService: MessageService,
        private titlecasePipe: TitleCasePipe,
        private financeUtilityService: FinanceUtilityService,
    ) { }

    ngOnInit(): void {
        this.initAccountForm();
        this.initGroupForm();
        this.financeObjectId = this.app.preLoadedObjects.find((obj) => obj.name == "Finance")?.id;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.addEditDialogType && changes.addEditDialogType.currentValue) {
            this.addEditDialogType = changes.addEditDialogType.currentValue;
            this.noRange = null;
            this.action = ObjectNames.AddAction;
            this.initAccountForm();
            
            if(this.addEditDialogType == AddEditDialogType.Add){
                this.removeNonPostingAccount();
            }
        }
        if (changes.allAccounts && changes.allAccounts.currentValue) {
            this.allAccounts = changes.allAccounts.currentValue;
            this.accountTypes = this.financeUtilityService.getAccountTypes(true, true, this.allAccounts, true, 1, maxChildLevelForChartOfAccountsCreate);
            this.nonPostingAccount = this.accountTypes?.find((ele) => ele?.displayValue?.toLowerCase() == "non posting");
            this.removeNonPostingAccount();
        }
        if (changes.editAccountData && changes.editAccountData.currentValue) {
            this.editAccountData = changes.editAccountData.currentValue;
            this.noRange = null;
            this.action = ObjectNames.EditAction;
            this.initAccountForm();
            this.showEditAccount(this.editAccountData?.parent, this.editAccountData?.child, this.editAccountData?.sub);
        }
    }
    updateFieldStates() {
        const isEditMode = this.addEditDialogType === this.dialogType.Edit;
        const isNameFieldDisabled = this.detailsData?.createdSource?.toLowerCase() === 'system' && this.addEditDialogType === this.dialogType.Edit;
        const isSubAccountTypeDisabled = this.detailsData?.level != 1 && this.addEditDialogType === this.dialogType.Edit;
        this.toggleControlState('parentFinancialAccountId', isEditMode);
        this.toggleControlState('subAccountTypeId', isSubAccountTypeDisabled);
        this.toggleControlState('name', isNameFieldDisabled);
    }
    toggleControlState(controlName: string, disable: boolean) {
        const control = this.accountForm.get(controlName);
        if (disable) {
            control?.disable();
        } else {
            control?.enable();
        }
    }
    initAccountForm() {
        this.accountForm = this.fb.group({
            id: [0],
            name: ["", [Validators.required, Validators.pattern(/^(?!\s*$).+/)]],
            subAccountTypeId: ["", Validators.required],
            parentFinancialAccountId: ["", Validators.required],
            financialAccountNumber: [null], // Validators.required
            displayAccountNumber: [null, Validators.required],
            status: [true],
        });
    }

    initGroupForm() {
        this.groupForm = this.fb.group({
            id: [0],
            name: ["", [Validators.required]],
        });
    }

    showEditAccount(parent?, child?, subChild?) {
        if(parent && parent?.name?.toLowerCase() == "non posting") {
            this.accountTypes.push(this.nonPostingAccount);
        } else {
            this.removeNonPostingAccount();
        }
        this.action = ObjectNames.EditAction;
        this.childType = false;
        this.parentType = false;
        this.disabledGroupForSubChild = false;
        if (subChild) {
            this.childType = true;
            this.disabledGroupForSubChild = true;
            this.detailsDataType = "subchild";
            this.detailsData = subChild;
            this.accountForm.patchValue(subChild);
            const targetNodeId = subChild.parentFinancialAccountId;
            const foundNode = this.financeUtilityService.findRecursiveChildObjects(this.accountTypes, targetNodeId);
            this.updateFieldStates();
            this.accountForm.controls["parentFinancialAccountId"].setValue(foundNode);
            this.accountForm.controls.status.setValue(subChild?.statusId == Status.Active ? true : false);
            this.changeChartGroup(child.accountTypeId, ObjectNames?.EditAction, subChild.subAccountTypeId);
        } else {
            this.detailsDataType = "child";
            this.detailsData = child;
            this.accountForm.patchValue(child);
            this.parentType = true;
            const targetNodeId = child.parentFinancialAccountId;
            const foundNode = this.financeUtilityService.findRecursiveChildObjects(this.accountTypes, targetNodeId);
            this.updateFieldStates();
            this.accountForm.controls["parentFinancialAccountId"].setValue(foundNode);
            this.accountForm.controls.status.setValue(child?.statusId == Status.Active ? true : false);
            this.changeChartGroup(parent?.accountTypeId, ObjectNames?.EditAction, child?.subAccountTypeId);
        }
        this.setDisplayNumber(parent?.id, parent, child, subChild, "edit");
    }

    changeChartGroup(id, action, subAccountTypeId?) {
        // if (id.node?.parent) {
        //     this.setNumberValidation(id.node?.parent?.value?.description);
        // } else {
        //     this.setNumberValidation(id.node?.value?.description);
        // }
        if (action == ObjectNames.AddAction) {
            this.setDisplayNumber(id);
        }
        const accountTypeId = action == ObjectNames.EditAction ? id : id.node.value.accountTypeId;
        this.getGroups(accountTypeId, id, action, subAccountTypeId);
    }

    removeNonPostingAccount() {
        if(this.accountTypes && this.accountTypes.length > 0) {
            this.accountTypes = this.accountTypes?.filter((ele) => ele?.displayValue?.toLowerCase() != "non posting");
        }
    }

    setDisplayNumber(id: any, parent?, child?, subChild?, action?) {
        this.parentId = id?.node?.id || id;
        if (this.action == ObjectNames.AddAction) {
            const ids = this.collectIds(id?.node);
            this.displayNumber = "";
            let currentGroup = null;
            for (let i = ids.length - 1; i >= 0; i--) {
                if (i === ids.length - 1) {
                    currentGroup = this.allAccounts?.find((ele) => ele.id === ids[i]);
                } else {
                    currentGroup = currentGroup?.childAccounts?.find((ele) => ele.id === ids[i]);
                }
                // if (currentGroup?.displayNumber && ids.length == 1) {
                //     this.displayNumber = currentGroup?.displayNumber;
                //     this.displayOperator = currentGroup?.displayOperator;
                // } else {
                this.displayNumber = currentGroup?.displayNumber && currentGroup?.displayOperator ? currentGroup?.displayNumber + currentGroup?.displayOperator : currentGroup?.displayNumber;
                this.displayOperator = currentGroup?.displayOperator;
                // }
            }
            this.accountForm.patchValue({ displayAccountNumber: this.displayNumber });
            this.isDisabled = this.accountForm.controls.displayAccountNumber.value == this.displayNumber;
        }
        if (action == "edit") {
            this.accountForm.patchValue({ displayAccountNumber: subChild?.displayNumber ?? child?.displayNumber ?? parent?.displayNumber });
            this.displayNumber = subChild?.displayNumber ?? child?.displayNumber ?? parent?.displayNumber;
            this.displayOperator = subChild?.displayOperator ?? child?.displayOperator ?? parent?.displayOperator;

            this.displayAccNumber = subChild?.displayAccountNumber ?? child?.displayAccountNumber ?? parent?.displayAccountNumber;
        }
    }

    ensureDigit(event: InputEvent): void {
        const input = event.target as HTMLInputElement;
        this.filteredGroup = this.allAccounts?.find((ele) => ele?.id == this.parentId);
        if (this.action == ObjectNames.AddAction) {
            if (input.value.endsWith('-') || input.value.endsWith(':')) {
                input.value = input.value.slice(0, -1);
            }
            if (!/^\d*$/.test(input.value.slice(this.displayNumber.length))) {
                input.value = input.value.replace(/[^0-9:-]/g, '');
                return
            }
            if (event.inputType === "deleteContentBackward" && (input.value == this.displayNumber || input.value.length <= this.displayNumber.length)) {
                input.value = this.displayNumber
            } else if ((input.value.length + 1 === this.displayNumber?.length) || !input.value.startsWith(this.displayNumber)) {
                input.value = this.displayNumber;
            }
            this.isDisabled = input.value == this.displayNumber;
        } else if (this.action == ObjectNames.EditAction) {
            if (input.value.endsWith('-') || input.value.endsWith(':')) {
                input.value = input.value.slice(0, -1);
            }
            if (!/^\d*$/.test(input.value.slice(this.displayNumber?.slice(0, this.displayNumber?.length - String(this.displayAccNumber).length).length))) {
                input.value = input.value.replace(/[^0-9:-]/g, '');;
                return
            }
            const str = this.displayNumber?.slice(0, this.displayNumber?.length - String(this.displayAccNumber).length)
            const editDisplayNumber = this.displayNumber?.slice(0, this.displayNumber?.length - String(this.displayAccNumber).length)
            if (event.inputType === "deleteContentBackward" && (input.value == str || input.value.length <= str.length)) {
                input.value = str
            }
            else if ((input.value.length + 1 === this.displayNumber?.length - String(this.displayAccNumber).length) || !input.value.startsWith(editDisplayNumber)) {
                input.value = this.displayNumber?.slice(0, this.displayNumber?.length - String(this.displayAccNumber).length);
            }
            this.isDisabled = input.value == this.displayNumber?.slice(0, this.displayNumber?.length - String(this.displayAccNumber).length);
        }
    }

    collectIds(obj: any): number[] {
        const ids: number[] = [];
        let current = obj;
        while (current) {
            if (current.id) {
                ids.push(current.id);
            }
            current = current.parent;
        }
        return ids;
    }

    getGroups(accountTypeId, id?, action?, subAccountTypeId?) {
        this.accountService.getAccountGroups(accountTypeId).subscribe((groups) => {
            this.chartGroupsData = groups;
            if (action == ObjectNames.EditAction) {
                const filteredGroup = this.chartGroupsData.filter((group) => group.id == subAccountTypeId)[0];
                this.accountForm.controls["subAccountTypeId"].setValue(filteredGroup);
            }
            if (action == ObjectNames.AddAction && id.node.parent) {
                const filteredGroup = this.chartGroupsData.filter((group) => group.id == id.node.value.subAccountTypeId)[0];
                this.accountForm.controls["subAccountTypeId"].setValue(filteredGroup);
                this.disabledGroupForSubChild = true;
            } else if (action == ObjectNames.AddAction && !id.node.parent) {
                this.disabledGroupForSubChild = false;
                if (this.chartGroupsData && this.chartGroupsData.length) {
                    this.accountForm.controls["subAccountTypeId"].setValue(this.chartGroupsData[0]);
                } else {
                    this.accountForm.controls["subAccountTypeId"].setValue("");
                }
            }

            // add new option to groups dropdown
            // this.chartGroupsData.push({ id: 0, name: "New Type" });
        });
    }

    saveGroup() {
        const group: IGroup = {
            id: 0,
            createdSource: this.app.userIP,
            updatedSource: this.app.userIP,
            createdBy: this.app.user.userId,
            updatedBy: this.app.user.userId,
            createdDate: this.app._saveDateTimeWithZone(new Date()),
            updatedDate: this.app._saveDateTimeWithZone(new Date()),
            name: this.groupForm.controls["name"].value,
            accountTypeNumber: 0,
            parentAccountTypeId: this.accountForm.controls.parentFinancialAccountId.value?.value?.accountTypeId,
            statusId: Status.Active,
            groupId: this.accountForm?.controls.parentFinancialAccountId?.value?.value?.accountType?.groupId || 0,
        };
        this.accountService.saveGroup(group).subscribe({
            next: (response) => {
                this.messageService.add({
                    severity: Severity.Success,
                    summary: this.titlecasePipe.transform(Severity.Success),
                    detail: "Group Added successfully...",
                });
                this.displayAddGroup = false;
                this.getGroups(this.accountForm.controls.parentFinancialAccountId.value?.value?.accountTypeId);
            },
            error: (error) => {
                this.messageService.add({
                    severity: Severity.Error,
                    summary: this.titlecasePipe.transform(Severity.Error),
                    detail: "Error occurred while saving data...",
                });
            },
        });
    }

    showAddGoup() {
        if (this.accountForm.controls["subAccountTypeId"].value?.name?.toLowerCase() == "new type") {
            this.displayAddGroup = true;
        }
    }

    hideAddGroup() {
        this.displayAddGroup = false;
        this.groupForm.reset();
    }

    setNumberValidation(value) {
        if (value && value != "string") {
            this.noRange = value;
            const rangeArr = this.noRange.split("-");
            if (rangeArr && rangeArr.length > 1) {
                this.accountForm.get("financialAccountNumber").setValidators([Validators.required, Validators.min(Number(rangeArr[0])), Validators.max(Number(rangeArr[1]))]);
                this.accountForm.get("financialAccountNumber").updateValueAndValidity();
            }
        }
    }

    saveChart() {
        let accTypeid;
        let financialAccId;
        if (this.addEditDialogType == AddEditDialogType.Add) {
            financialAccId = 0;
            accTypeid = this.accountForm.controls.parentFinancialAccountId.value?.value?.accountTypeId;
        } else {
            financialAccId = this.detailsData.id;
            accTypeid = this.accountForm.controls.parentFinancialAccountId.value?.value?.accountTypeId;
        }

        if (this.addEditDialogType == AddEditDialogType.Add) {
            this.addAccount();
        } else {
            this.updateAccount();
        }
    }

    addAccount() {
        const data: IFinanceAccount = {
            id: 0,
            createdSource: this.app.userIP,
            updatedSource: this.app.userIP,
            createdBy: this.app.user.userId,
            updatedBy: this.app.user.userId,
            createdDate: this.app._saveDateTimeWithZone(new Date()),
            updatedDate: this.app._saveDateTimeWithZone(new Date()),
            organizationId: this.app.companyId,
            name: this.accountForm.controls.name.value,
            accountTypeId: this.accountForm.controls.parentFinancialAccountId.value?.value?.accountTypeId || 0,
            accountType: null,
            subAccountTypeId: this.accountForm.controls.subAccountTypeId.value?.id || 0,
            subAccountType: null,
            parentFinancialAccountId: this.accountForm.controls.parentFinancialAccountId.value?.value?.id || 0,
            financialAccountNumber: this.accountForm.controls.financialAccountNumber.value || this.filteredGroup?.financialAccountNumber || this.accountForm.controls["displayAccountNumber"].value?.replace(/\D/g, "") || 0,
            financialStatementId: this.accountForm.controls.parentFinancialAccountId.value?.value?.financialStatementId || 0,
            description: this.noRange,
            qbdAccountMap: "",
            qboAccountMap: "",
            qboAccountMapId: 0,
            statusId: this.accountForm.controls.status.value ? Status.Active : Status.InActive,
            objectId: this.financeObjectId,
            isDefault: false,
            isDebit: this.accountForm.controls.parentFinancialAccountId.value?.value?.isDebit ?? false,
            isBank: this.accountForm.controls.parentFinancialAccountId.value?.value?.isBank ?? false,
            isCreditCard: this.accountForm.controls.parentFinancialAccountId.value?.value?.isCreditCard ?? false,
            reconcileEnabled: this.accountForm.controls.parentFinancialAccountId.value?.value?.reconcileEnabled || false,
            displayAccountNumber: this.accountForm.controls.displayAccountNumber.value?.slice(this.displayNumber?.length),
            displayNumber: this.accountForm.controls.displayAccountNumber.value,
            displayOperator: this.displayNumber[this.displayNumber?.length - 1],
        };
        this.accountService.saveAccount(data).subscribe({
            next: (res) => {
                this.messageService.add({
                    severity: Severity.Success,
                    summary: this.titlecasePipe.transform(Severity.Success),
                    detail: "Account Added successfully...",
                });
                this.onSuccessfullAddEdit.emit({});
            },
            error: (error) => {
                this.messageService.add({
                    severity: Severity.Error,
                    summary: this.titlecasePipe.transform(Severity.Error),
                    detail: error?.error?.Message ?? "Error occurred while saving data...",
                });
            },
        });
    }

    updateAccount() {
        let displayAccountNumber = this.accountForm.controls.displayAccountNumber.value?.slice(this.displayNumber?.length - String(this.displayAccNumber).length);
        const data: IFinanceAccount = {
            id: this.accountForm.controls.id.value,
            createdSource: this.detailsData.createdSource,
            updatedSource: this.app.userIP,
            createdBy: this.detailsData.createdBy,
            updatedBy: this.app.user.userId,
            createdDate: this.detailsData.createdDate,
            updatedDate: this.app._saveDateTimeWithZone(new Date()),
            organizationId: this.app.companyId,
            name: this.accountForm.controls.name.value,
            accountTypeId: this.accountForm.controls.parentFinancialAccountId.value?.value?.accountTypeId || 0,
            accountType: null,
            subAccountTypeId: this.accountForm.controls.subAccountTypeId.value?.id || 0,
            subAccountType: null,
            parentFinancialAccountId: this.accountForm.controls.parentFinancialAccountId.value?.value?.id || 0,
            financialAccountNumber: this.detailsData?.createdSource === 'system' ? this.accountForm.controls.financialAccountNumber.value || this.accountForm.controls["displayAccountNumber"].value?.replace(/\D/g, "") || 0 : displayAccountNumber,
            financialStatementId: this.accountForm.controls.parentFinancialAccountId.value?.value?.financialStatementId || 0,
            description: this.noRange,
            qbdAccountMap: "",
            qboAccountMap: "",
            qboAccountMapId: 0,
            statusId: this.accountForm.controls.status.value ? Status.Active : Status.InActive,
            objectId: this.financeObjectId,
            isDefault: this.detailsData?.isDefault ?? false,
            isDebit: this.accountForm.controls.parentFinancialAccountId.value?.value?.isDebit ?? false,
            isBank: this.accountForm.controls.parentFinancialAccountId.value?.value?.isBank ?? false,
            isCreditCard: this.accountForm.controls.parentFinancialAccountId.value?.value?.isCreditCard ?? false,
            reconcileEnabled: this.detailsData.reconcileEnabled,
            displayAccountNumber: this.accountForm.controls.displayAccountNumber.value?.slice(this.displayNumber?.length - String(this.displayAccNumber).length),
            displayNumber: this.accountForm.controls.displayAccountNumber.value,
            displayOperator: this.displayOperator,
        };
        this.accountService.editAccount(data).subscribe({
            next: (res) => {
                this.messageService.add({
                    severity: Severity.Success,
                    summary: this.titlecasePipe.transform(Severity.Success),
                    detail: "Account Updated successfully...",
                });
                this.onSuccessfullAddEdit.emit({});
            },
            error: (error) => {
                this.messageService.add({
                    severity: Severity.Error,
                    summary: this.titlecasePipe.transform(Severity.Error),
                    detail: error?.error?.Message ?? "Error occurred while saving data...",
                });
            },
        });
    }

    closeDialog() {
        this.onCancel.emit({});
    }
}
